import { useState, useEffect } from 'react';
import { useLogAnalyticsRetentionCalc } from '../../hooks/useCalculate';
import { useAnalytics } from '../../hooks/useAnalytics';
import { Disclaimer } from '../shared/Disclaimer';
import { FieldTooltip } from '../shared/FieldTooltip';
import { FeedbackModal } from '../shared/FeedbackModal';
import { ResourcesSection } from '../shared/ResourcesSection';
import { ShareButton } from '../shared/ShareButton';
import type { LogAnalyticsRetentionRequest } from '../../api/types';

const FREE_DAYS_BASE = 31;
const FREE_DAYS_SENTINEL = 90;

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      aria-pressed={checked}
      style={checked
        ? { backgroundColor: 'var(--color-primary)', border: '1px solid var(--color-primary)' }
        : { backgroundColor: 'var(--outline-variant)', border: '1px solid var(--outline)' }}
      className="w-12 h-6 rounded-full flex items-center px-1 transition-colors"
    >
      <span
        className="w-4 h-4 rounded-full transition-transform bg-white"
        style={{ transform: checked ? 'translateX(24px)' : 'translateX(0)' }}
      />
    </button>
  );
}

export default function LogAnalyticsRetentionCalc() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [inputs, setInputs] = useState<LogAnalyticsRetentionRequest>({
    totalWorkspaceSizeGB: 100,
    interactiveRetentionDays: 31,
    archiveRetentionDays: 0,
    dailyIngestionGB: 10,
    sentinelEnabled: false,
  });

  const set = (partial: Partial<LogAnalyticsRetentionRequest>) =>
    setInputs((prev) => ({ ...prev, ...partial }));

  const { data, isLoading, isError } = useLogAnalyticsRetentionCalc(inputs);
  const { trackCalculatorUsed } = useAnalytics();
  useEffect(() => {
    if (data?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'log-analytics', result_gbp: data.monthly_estimate })
    }
  }, [data?.monthly_estimate])
  const freeDays = inputs.sentinelEnabled ? FREE_DAYS_SENTINEL : FREE_DAYS_BASE;

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_380px] gap-8 w-full">
      {/* ── Left: Controls ── */}
      <div className="flex flex-col gap-6">
        <div className={`bg-surface-container-high micro-gradient ghost-border rounded-lg p-6 transition-opacity ${isLoading ? 'opacity-70' : ''}`}>
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-6">
            <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>storage</span>
            Retention Configuration
          </h2>

          {/* Workspace size */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex justify-between items-end">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Total Workspace Size</label>
              <span className="text-xl font-bold font-mono text-primary">{inputs.totalWorkspaceSizeGB.toLocaleString()} GB</span>
            </div>
            <input
              type="range"
              min={0} max={10000} step={100}
              value={inputs.totalWorkspaceSizeGB}
              onChange={(e) => set({ totalWorkspaceSizeGB: Number(e.target.value) })}
              className="w-full h-1 bg-surface-container-highest rounded-lg appearance-none cursor-pointer accent-primary"
            />
          </div>

          {/* Interactive retention */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex justify-between items-end">
              <div className="flex items-center gap-2">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Interactive Retention <FieldTooltip content="Days data is queryable via KQL at full speed. First 31 days are free for Analytics tables; charged per GB/month beyond that." /></label>
                {inputs.interactiveRetentionDays <= freeDays && (
                  <span className="text-[9px] font-bold text-[#107c10] bg-[#107c10]/10 px-2 py-0.5 border border-[#107c10]/30 uppercase">FREE</span>
                )}
              </div>
              <span className="text-xl font-bold font-mono">{inputs.interactiveRetentionDays} days</span>
            </div>
            <input
              type="range"
              min={1} max={730} step={1}
              value={inputs.interactiveRetentionDays}
              onChange={(e) => set({ interactiveRetentionDays: Number(e.target.value) })}
              className="w-full h-1 bg-surface-container-highest rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <p className="text-[10px] text-outline italic">
              First {freeDays} days free{inputs.sentinelEnabled ? ' (extended to 90 days with Sentinel)' : '. Enable Sentinel to extend to 90 days free'}.
            </p>
          </div>

          {/* Archive retention */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex justify-between items-end">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Archive Retention <FieldTooltip content="Cheap long-term storage after interactive retention ends. Data is not directly queryable — you must run a restore or search job." /></label>
              <span className="text-xl font-bold font-mono">{inputs.archiveRetentionDays} days</span>
            </div>
            <input
              type="range"
              min={0} max={2557} step={30}
              value={inputs.archiveRetentionDays}
              onChange={(e) => set({ archiveRetentionDays: Number(e.target.value) })}
              className="w-full h-1 bg-surface-container-highest rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <p className="text-[10px] text-outline italic">Up to 7 years archive retention at £0.0185/GB/month.</p>
          </div>

          {/* Daily ingestion */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex justify-between items-end">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Daily Ingestion Rate</label>
              <span className="text-xl font-bold font-mono">{inputs.dailyIngestionGB} GB/day</span>
            </div>
            <input
              type="range"
              min={0} max={500} step={1}
              value={inputs.dailyIngestionGB}
              onChange={(e) => set({ dailyIngestionGB: Number(e.target.value) })}
              className="w-full h-1 bg-surface-container-highest rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <p className="text-[10px] text-outline italic">Used for long-term cost projections — workspace grows by this amount daily.</p>
          </div>

          {/* Sentinel toggle */}
          <div className="flex items-center justify-between p-4 bg-surface-container-lowest/50 border border-outline-variant/50 rounded-sm">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-[#e8b000]" style={{ fontVariationSettings: "'FILL' 1" }}>security</span>
              <div>
                <p className="text-sm font-bold">Microsoft Sentinel</p>
                <p className="text-[10px] text-outline">Extends free interactive retention to 90 days</p>
              </div>
            </div>
            <Toggle checked={inputs.sentinelEnabled} onChange={() => set({ sentinelEnabled: !inputs.sentinelEnabled })} />
          </div>
        </div>
      </div>

      {/* ── Right: Results ── */}
      <div className="flex flex-col gap-6">
        <div className="sticky top-[72px] flex flex-col gap-6">
          <div className="bg-surface-container-high micro-gradient border-l-2 border-primary rounded-lg overflow-hidden">
            <div className="p-6">
              <h3 className="font-mono text-[11px] text-outline uppercase tracking-widest mb-2">Monthly Retention Cost</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold font-headline">
                  {isLoading ? '...' : isError ? 'Error' : `£${data?.monthly_estimate.toFixed(2)}`}
                </span>
                <span className="text-outline text-sm">/mo</span>
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  title="Report an issue with this estimate"
                  className="ml-1 transition-colors"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  <span className="material-symbols-outlined text-[18px]">help</span>
                </button>
              </div>
            </div>
            <div className="bg-surface-container-lowest/50 p-6 space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">list_alt</span>
                Cost Breakdown
              </h4>
              <ul className="space-y-3">
                {data?.breakdown.map((item, idx) => (
                  <li key={idx} className="flex flex-col gap-0.5">
                    <div className="flex justify-between items-center font-mono text-xs">
                      <span className="text-outline">{item.label}</span>
                      <span className={`font-bold ${item.isFree ? 'text-[#107c10]' : ''}`}>
                        {item.isFree ? 'FREE' : `£${item.value.toFixed(2)}`}
                      </span>
                    </div>
                    {item.note && <p className="text-[10px] text-outline/60 italic">{item.note}</p>}
                  </li>
                ))}
                {!data && !isLoading && <p className="text-[10px] text-outline text-center py-4">Adjust inputs to see pricing</p>}
                {data && (
                  <li className="pt-3 border-t border-outline-variant flex justify-between items-center font-mono text-sm">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-primary">£{data.monthly_estimate.toFixed(2)}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Multi-year projections */}
          {data && (
            <div className="bg-surface-container-high ghost-border rounded-lg p-5">
              <h4 className="text-[11px] font-mono text-outline uppercase tracking-wider mb-4">Long-term Cost Projection</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-surface-container-lowest/50 border border-outline-variant p-4 rounded-sm">
                  <p className="text-[10px] text-outline uppercase tracking-wider mb-1">1-Year Total</p>
                  <p className="text-2xl font-bold font-mono text-on-surface">£{data.total_cost_1_year.toFixed(0)}</p>
                  <p className="text-[9px] text-outline mt-1">As workspace grows</p>
                </div>
                <div className="bg-surface-container-lowest/50 border border-outline-variant p-4 rounded-sm">
                  <p className="text-[10px] text-outline uppercase tracking-wider mb-1">3-Year Total</p>
                  <p className="text-2xl font-bold font-mono text-on-surface">£{data.total_cost_3_years.toFixed(0)}</p>
                  <p className="text-[9px] text-outline mt-1">As workspace grows</p>
                </div>
              </div>
              {data.warnings && data.warnings.length > 0 && (
                <div className="mt-4 space-y-2">
                  {data.warnings.map((w, i) => (
                    <p key={i} className="text-[10px] text-outline flex items-start gap-2">
                      <span className="material-symbols-outlined text-[14px] shrink-0 mt-0.5">info</span>
                      {w}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="bg-surface-container-lowest/50 border border-outline-variant p-4 text-[10px] text-outline leading-relaxed">
            <Disclaimer />
          </div>
          <div className="pb-2">
            <ShareButton inputs={inputs} result={data ?? null} service="log-analytics" />
          </div>
          <ResourcesSection service="log-analytics" />
        </div>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="log-analytics/retention"
          estimateValue={data ? `£${data.monthly_estimate.toFixed(2)}/mo` : 'N/A'}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </div>
  );
}
